import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import "./TheShelf.scss";
import NookDragon, { unlockNookWhoosh } from "./NookDragon";
import NookArcher from "./NookArcher";

// Fantasy — highest/top candle in the nook (left). Click → green, no creature.
const TOP_CANDLE_SHELF = 0;
// Literary Fiction shelf — the middle of the three candles (right, large).
const CENTER_CANDLE_SHELF = 2;
// Philosophy — last shelf, lowest candle in the nook (left, small).
const BOTTOM_CANDLE_SHELF = 6;

// ─────────────────────────────────────────────────────────────────────────────
// BOOK NOOK — a snapshot of the books that shaped Ashly's writing.
//
// HOW TO EDIT:
//   • Each entry in SECTIONS is one shelf: { category, books: [...] }.
//     The category name is shown as a brass label on the wooden shelf board.
//   • Each book needs at minimum { title, author }.
//   • Optional per-book fields:
//       spineTitle – a shorter title to print on the (narrow) spine.
//       note       – a personal line shown in the pop-up panel. Add your own!
//       goodreads  – exact Goodreads book page URL (preferred over search).
//       cover      – cover image for the detail panel (local /images/nook-covers).
//       color / height / width / material / foil
//                  – override the auto-generated spine look for one book.
//
//   The spine color, texture (leather/cloth), size, and gold foil are generated
//   automatically from each title so the shelf looks naturally varied.
//   If `cover` is missing, the panel uses the colored title/author fallback.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    category: "Fantasy",
    books: [
      {
        title: "The Lord of the Rings",
        author: "J.R.R. Tolkien",
        goodreads: "https://www.goodreads.com/book/show/33.The_Lord_of_the_Rings",
        cover: "/images/nook-covers/the-lord-of-the-rings.jpg",
      },
      {
        title: "The Hobbit",
        author: "J.R.R. Tolkien",
        goodreads: "https://www.goodreads.com/book/show/5907.The_Hobbit",
        cover: "/images/nook-covers/the-hobbit.jpg",
      },
      {
        title: "The Wind in the Willows",
        author: "Kenneth Grahame",
        goodreads: "https://www.goodreads.com/book/show/5659.The_Wind_in_the_Willows",
        cover: "/images/nook-covers/the-wind-in-the-willows.jpg",
      },
      {
        title: "Harry Potter",
        author: "J.K. Rowling",
        goodreads:
          "https://www.goodreads.com/book/show/3.Harry_Potter_and_the_Sorcerer_s_Stone",
        cover: "/images/nook-covers/harry-potter.jpg",
      },
      {
        title: "The Name of the Wind",
        author: "Patrick Rothfuss",
        goodreads: "https://www.goodreads.com/book/show/186074.The_Name_of_the_Wind",
        cover: "/images/nook-covers/the-name-of-the-wind.jpg",
      },
      {
        title: "The Shadow of the Wind",
        author: "Carlos Ruiz Zafón",
        goodreads: "https://www.goodreads.com/book/show/1232.The_Shadow_of_the_Wind",
        cover: "/images/nook-covers/the-shadow-of-the-wind.jpg",
      },
      {
        title: "The Golden Compass",
        author: "Philip Pullman",
        goodreads: "https://www.goodreads.com/book/show/119322.The_Golden_Compass",
        cover: "/images/nook-covers/the-golden-compass.jpg",
      },
      {
        title: "The NeverEnding Story",
        author: "Michael Ende",
        goodreads: "https://www.goodreads.com/book/show/27712.The_Neverending_Story",
        cover: "/images/nook-covers/the-neverending-story.jpg",
      },
      {
        title: "A Court of Thorns and Roses",
        spineTitle: "A Court of Thorns & Roses",
        author: "Sarah J. Maas",
        goodreads:
          "https://www.goodreads.com/book/show/50659467-a-court-of-thorns-and-roses",
        cover: "/images/nook-covers/a-court-of-thorns-and-roses.jpg",
      },
    ],
  },
  {
    category: "Science Fiction",
    books: [
      {
        title: "Dune",
        author: "Frank Herbert",
        goodreads: "https://www.goodreads.com/book/show/44767458-dune",
        cover: "/images/nook-covers/dune.jpg",
      },
      {
        title: "Ender's Game",
        author: "Orson Scott Card",
        goodreads: "https://www.goodreads.com/book/show/375802.Ender_s_Game",
        cover: "/images/nook-covers/ender-s-game.jpg",
      },
      {
        title: "Speaker for the Dead",
        author: "Orson Scott Card",
        goodreads: "https://www.goodreads.com/book/show/7967.Speaker_for_the_Dead",
        cover: "/images/nook-covers/speaker-for-the-dead.jpg",
      },
      {
        title: "Ender's Shadow",
        author: "Orson Scott Card",
        goodreads: "https://www.goodreads.com/book/show/9532.Ender_s_Shadow",
        cover: "/images/nook-covers/ender-s-shadow.jpg",
      },
      {
        title: "Project Hail Mary",
        author: "Andy Weir",
        goodreads: "https://www.goodreads.com/book/show/54493401-project-hail-mary",
        cover: "/images/nook-covers/project-hail-mary.jpg",
      },
      {
        title: "The Martian",
        author: "Andy Weir",
        goodreads: "https://www.goodreads.com/book/show/18007564-the-martian",
        cover: "/images/nook-covers/the-martian.jpg",
      },
      {
        title: "The War of the Worlds",
        author: "H.G. Wells",
        goodreads: "https://www.goodreads.com/book/show/8909.The_War_of_the_Worlds",
        cover: "/images/nook-covers/the-war-of-the-worlds.jpg",
      },
    ],
  },
  {
    category: "Literary Fiction & Classics",
    books: [
      {
        title: "Pride and Prejudice",
        author: "Jane Austen",
        goodreads: "https://www.goodreads.com/book/show/1885.Pride_and_Prejudice",
        cover: "/images/nook-covers/pride-and-prejudice.jpg",
      },
      {
        title: "1984",
        author: "George Orwell",
        goodreads: "https://www.goodreads.com/book/show/61439040-1984",
        cover: "/images/nook-covers/1984.jpg",
      },
      {
        title: "Animal Farm",
        author: "George Orwell",
        goodreads: "https://www.goodreads.com/book/show/170448.Animal_Farm",
        cover: "/images/nook-covers/animal-farm.jpg",
      },
      {
        title: "The Grapes of Wrath",
        author: "John Steinbeck",
        goodreads: "https://www.goodreads.com/book/show/18114322-the-grapes-of-wrath",
        cover: "/images/nook-covers/the-grapes-of-wrath.jpg",
      },
      {
        title: "Lord of the Flies",
        author: "William Golding",
        goodreads: "https://www.goodreads.com/book/show/7624.Lord_of_the_Flies",
        cover: "/images/nook-covers/lord-of-the-flies.jpg",
      },
      {
        title: "To Kill a Mockingbird",
        author: "Harper Lee",
        goodreads: "https://www.goodreads.com/book/show/2657.To_Kill_a_Mockingbird",
        cover: "/images/nook-covers/to-kill-a-mockingbird.jpg",
      },
      {
        title: "The Picture of Dorian Gray",
        author: "Oscar Wilde",
        goodreads: "https://www.goodreads.com/book/show/5297.The_Picture_of_Dorian_Gray",
        cover: "/images/nook-covers/the-picture-of-dorian-gray.jpg",
      },
      {
        title: "Where the Crawdads Sing",
        author: "Delia Owens",
        goodreads: "https://www.goodreads.com/book/show/36809135-where-the-crawdads-sing",
        cover: "/images/nook-covers/where-the-crawdads-sing.jpg",
      },
      {
        title: "The Girl with the Dragon Tattoo",
        author: "Stieg Larsson",
        goodreads:
          "https://www.goodreads.com/book/show/2429135.The_Girl_With_the_Dragon_Tattoo",
        cover: "/images/nook-covers/the-girl-with-the-dragon-tattoo.jpg",
      },
      {
        title: "The Hunger Games",
        author: "Suzanne Collins",
        goodreads: "https://www.goodreads.com/book/show/2767052-the-hunger-games",
        cover: "/images/nook-covers/the-hunger-games.jpg",
      },
    ],
  },
  {
    category: "Science, Mind & Technology",
    books: [
      {
        title: "Thinking, Fast and Slow",
        author: "Daniel Kahneman",
        goodreads: "https://www.goodreads.com/book/show/11468377-thinking-fast-and-slow",
        cover: "/images/nook-covers/thinking-fast-and-slow.jpg",
      },
      {
        title: "Gödel, Escher, Bach: An Eternal Golden Braid",
        spineTitle: "Gödel, Escher, Bach",
        author: "Douglas Hofstadter",
        goodreads: "https://www.goodreads.com/book/show/24113.G_del_Escher_Bach",
        cover: "/images/nook-covers/g-del-escher-bach-an-eternal-golden-braid.jpg",
      },
      {
        title: "I Am a Strange Loop",
        author: "Douglas Hofstadter",
        goodreads: "https://www.goodreads.com/book/show/123471.I_Am_a_Strange_Loop",
        cover: "/images/nook-covers/i-am-a-strange-loop.jpg",
      },
      {
        title: "Ghost in the Wires",
        author: "Kevin Mitnick",
        goodreads: "https://www.goodreads.com/book/show/10256723-ghost-in-the-wires",
        cover: "/images/nook-covers/ghost-in-the-wires.jpg",
      },
      {
        title: "Fair Play",
        author: "James M. Olson",
        goodreads: "https://www.goodreads.com/book/show/176691.Fair_Play",
        cover: "/images/nook-covers/fair-play.jpg",
      },
      {
        title: "The Lost City of the Monkey God",
        spineTitle: "Lost City of the Monkey God",
        author: "Douglas Preston",
        goodreads:
          "https://www.goodreads.com/book/show/40873920-the-lost-city-of-the-monkey-god",
        cover: "/images/nook-covers/the-lost-city-of-the-monkey-god.jpg",
      },
      {
        title: "System Error",
        author: "Rob Reich, Mehran Sahami & Jeremy M. Weinstein",
        goodreads: "https://www.goodreads.com/book/show/56382402-system-error",
        cover: "/images/nook-covers/system-error.jpg",
      },
      {
        title: "Creativity, Inc.",
        author: "Ed Catmull",
        goodreads: "https://www.goodreads.com/book/show/18077903-creativity-inc",
        cover: "/images/nook-covers/creativity-inc.jpg",
      },
      {
        title: "The Art of Piano Playing",
        author: "Heinrich Neuhaus",
        goodreads: "https://www.goodreads.com/book/show/195697.The_Art_of_Piano_Playing",
        cover: "/images/nook-covers/the-art-of-piano-playing.jpg",
      },
    ],
  },
  {
    category: "Leadership & Exploration",
    books: [
      {
        title: "Steve Jobs",
        author: "Walter Isaacson",
        goodreads: "https://www.goodreads.com/book/show/11084145-steve-jobs",
        cover: "/images/nook-covers/steve-jobs.jpg",
      },
      {
        title: "The Last Lion",
        author: "William Manchester",
        goodreads: "https://www.goodreads.com/book/show/19809.The_Last_Lion",
        cover: "/images/nook-covers/the-last-lion.jpg",
      },
      {
        title: "The Rise of Theodore Roosevelt",
        author: "Edmund Morris",
        goodreads:
          "https://www.goodreads.com/book/show/40929.The_Rise_of_Theodore_Roosevelt",
        cover: "/images/nook-covers/the-rise-of-theodore-roosevelt.jpg",
      },
      {
        title: "Eisenhower in War and Peace",
        author: "Jean Edward Smith",
        goodreads:
          "https://www.goodreads.com/book/show/11958983-eisenhower-in-war-and-peace",
        cover: "/images/nook-covers/eisenhower-in-war-and-peace.jpg",
      },
      {
        title: "Endurance: Shackleton's Incredible Voyage",
        spineTitle: "Endurance",
        author: "Alfred Lansing",
        goodreads: "https://www.goodreads.com/book/show/139069.Endurance",
        cover: "/images/nook-covers/endurance-shackleton-s-incredible-voyage.jpg",
      },
      {
        title:
          "In the Kingdom of Ice: The Grand and Terrible Polar Voyage of the USS Jeannette",
        spineTitle: "In the Kingdom of Ice",
        author: "Hampton Sides",
        goodreads: "https://www.goodreads.com/book/show/20897517-in-the-kingdom-of-ice",
        cover: "/images/nook-covers/in-the-kingdom-of-ice-the-grand-and-terrible-polar-voyage-of-the-uss-j.jpg",
      },
    ],
  },
  {
    category: "Memoir & Humor",
    books: [
      {
        title: "Man's Search for Meaning",
        author: "Viktor E. Frankl",
        goodreads: "https://www.goodreads.com/book/show/4069.Man_s_Search_for_Meaning",
        cover: "/images/nook-covers/man-s-search-for-meaning.jpg",
      },
      {
        title: "Hyperbole and a Half",
        author: "Allie Brosh",
        goodreads: "https://www.goodreads.com/book/show/17571564-hyperbole-and-a-half",
        cover: "/images/nook-covers/hyperbole-and-a-half.jpg",
      },
      {
        // Shelf label keeps your wording; Goodreads title is "Solutions and Other Problems".
        title: "Problems and Other Solutions",
        author: "Allie Brosh",
        goodreads:
          "https://www.goodreads.com/book/show/51323365-solutions-and-other-problems",
        cover: "/images/nook-covers/problems-and-other-solutions.jpg",
      },
      {
        title: "The Boy, the Mole, the Fox and the Horse",
        spineTitle: "The Boy, the Mole…",
        author: "Charlie Mackesy",
        goodreads:
          "https://www.goodreads.com/book/show/43708884-the-boy-the-mole-the-fox-and-the-horse",
        cover: "/images/nook-covers/the-boy-the-mole-the-fox-and-the-horse.jpg",
      },
    ],
  },
  {
    category: "Philosophy",
    books: [
      {
        title: "The Republic",
        author: "Plato",
        goodreads: "https://www.goodreads.com/book/show/30289.The_Republic",
        cover: "/images/nook-covers/the-republic.jpg",
      },
      {
        title: "Poetics",
        author: "Aristotle",
        goodreads: "https://www.goodreads.com/book/show/13270.Poetics",
        cover: "/images/nook-covers/poetics.jpg",
      },
      {
        title: "Nicomachean Ethics",
        author: "Aristotle",
        goodreads: "https://www.goodreads.com/book/show/19068.The_Nicomachean_Ethics",
        cover: "/images/nook-covers/nicomachean-ethics.jpg",
      },
      {
        title: "Meditations",
        author: "Marcus Aurelius",
        goodreads: "https://www.goodreads.com/book/show/30659.Meditations",
        cover: "/images/nook-covers/meditations.jpg",
      },
      {
        title: "Ethics",
        author: "Baruch Spinoza",
        goodreads: "https://www.goodreads.com/book/show/205218.Ethics",
        cover: "/images/nook-covers/ethics.jpg",
      },
      {
        title: "Thus Spoke Zarathustra",
        author: "Friedrich Nietzsche",
        goodreads: "https://www.goodreads.com/book/show/51893.Thus_Spoke_Zarathustra",
        cover: "/images/nook-covers/thus-spoke-zarathustra.jpg",
      },
      {
        title: "The Art of War",
        author: "Sun Tzu",
        goodreads: "https://www.goodreads.com/book/show/10534.The_Art_of_War",
        cover: "/images/nook-covers/the-art-of-war.jpg",
      },
    ],
  },
];

// Saturated antique-library palette — mid-to-deep jewel tones and warm
// accents where cream/gilt foil stays readable by candlelight.
const SPINE_COLORS = [
  "#1a4a38", // deep emerald
  "#244f3a", // forest green
  "#2f5c48", // mossy pine
  "#1a3a6e", // sapphire
  "#2a2f6a", // indigo
  "#1e4f5c", // deep teal
  "#2a5f6e", // sea teal
  "#6b1c28", // oxblood
  "#8a1f2e", // crimson
  "#5c1a36", // wine
  "#7a2848", // burgundy rose
  "#6b3a18", // deep amber
  "#7a4a1c", // burnt ochre
  "#5c4020", // walnut
  "#4a2a5c", // plum
  "#5c3a72", // violet
  "#3a4a28", // olive
  "#4a5c3a", // sage
  "#2a2824", // charcoal umber
  "#32282e", // charcoal plum
  "#3a2a24", // charcoal brown
  "#3a2e48", // dusk violet
];

const BASE_BOOK_HEIGHT = 310;

// Relative luminance (sRGB) — used to pick dark ink on light spines.
function spineLuminance(hex) {
  const n = hex.replace("#", "");
  if (n.length !== 6) return 0;
  const toLin = (c) => {
    const v = parseInt(c, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const r = toLin(n.slice(0, 2));
  const g = toLin(n.slice(2, 4));
  const b = toLin(n.slice(4, 6));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Stable FNV-1a hash so each title always gets the same spine look.
function hashStr(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function visualFor(book) {
  const h = hashStr(book.title);
  const color = book.color || SPINE_COLORS[h % SPINE_COLORS.length];
  // Light / mid-tan spines can't carry cream or gold foil — use dark ink.
  const lightSpine =
    book.lightSpine !== undefined
      ? book.lightSpine
      : spineLuminance(color) > 0.18;
  // Height varies ~5–13% around the base, stable per title → ~280–350px.
  // (Clamped so extremes stay readable as irregular spines, not dwarfs/giants.)
  const variancePct = 0.05 + ((h % 9) / 8) * 0.08; // 5%–13%
  const dir = (h >>> 5) % 2 === 0 ? 1 : -1;
  const rawHeight =
    book.height || Math.round(BASE_BOOK_HEIGHT * (1 + dir * variancePct));
  const height = book.height || Math.min(350, Math.max(280, rawHeight));
  return {
    color,
    height,
    width: book.width || 46 + ((h >>> 7) % 20), // 46–65px
    material: book.material || ((h >>> 11) % 3 === 0 ? "cloth" : "leather"),
    // Foil only on dark spines; light spines get solid dark lettering instead.
    foil: lightSpine
      ? false
      : book.foil !== undefined
        ? book.foil
        : (h >>> 13) % 5 < 2, // ~40%
    lightSpine,
  };
}

function goodreadsUrl(book) {
  if (book.goodreads) return book.goodreads;
  return (
    "https://www.goodreads.com/search?q=" +
    encodeURIComponent(`${book.title} ${book.author}`)
  );
}

function coverSrc(url) {
  if (!url) return url;
  if (url.includes("covers.openlibrary.org") && !url.includes("default=")) {
    return `${url}${url.includes("?") ? "&" : "?"}default=false`;
  }
  return url;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

// ── Easter egg: CANDLE ───────────────────────────────────────────────────────
// Where: sits on the shelf board (sibling of .shelf-books, not inside it) so
//        overflow on the book row cannot clip the flame / smoke.
// Look:  top shelf LEFT — ivory pillar + brass holder (also mid shelves).
// Click: dramatic flicker + smoke wisps + brief page dim.
function NookBurnOverlay({ raging }) {
  const canvasRef = useRef(null);
  const embers = useMemo(
    () =>
      Array.from({ length: 56 }, (_, i) => ({
        id: i,
        x: randomBetween(1, 99),
        delay: randomBetween(0, 1.8),
        duration: randomBetween(1.1, 2.8),
        size: randomBetween(3, 9),
        drift: randomBetween(-50, 50),
      })),
    []
  );
  const tongues = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        id: i,
        left: 3 + i * 7 + randomBetween(-2, 2),
        delay: randomBetween(0, 0.4),
        height: randomBetween(42, 78),
        width: randomBetween(8, 16),
      })),
    []
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const parent = canvas.parentElement;
    let raf = 0;
    let alive = true;
    const columns = Array.from({ length: 28 }, () => ({
      h: 0.15 + Math.random() * 0.35,
      w: 0.8 + Math.random() * 0.6,
      phase: Math.random() * Math.PI * 2,
      speed: 0.008 + Math.random() * 0.012,
    }));

    const resize = () => {
      const w = parent ? parent.clientWidth : 800;
      const h = parent ? parent.clientHeight : 1200;
      canvas.width = Math.max(1, w);
      canvas.height = Math.max(1, h);
    };
    resize();

    const draw = (now) => {
      if (!alive) return;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const t = now * 0.001;
      const surge = raging ? 1 : 0.55;
      columns.forEach((col, i) => {
        const x = ((i + 0.5) / columns.length) * w;
        const wave = 0.5 + 0.5 * Math.sin(t * 3.2 + col.phase);
        const fh = h * (col.h + wave * 0.35) * surge;
        const fw = (w / columns.length) * 2.4 * col.w;
        const g = ctx.createLinearGradient(x, h, x, h - fh);
        g.addColorStop(0, "rgba(232, 255, 242, 0.95)");
        g.addColorStop(0.18, "rgba(125, 255, 163, 0.85)");
        g.addColorStop(0.5, "rgba(46, 204, 113, 0.55)");
        g.addColorStop(1, "rgba(11, 61, 46, 0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(x - fw * 0.35, h);
        ctx.quadraticCurveTo(
          x + Math.sin(t * 4 + col.phase) * 12,
          h - fh * 0.55,
          x,
          h - fh
        );
        ctx.quadraticCurveTo(
          x - Math.sin(t * 3.4 + col.phase) * 10,
          h - fh * 0.45,
          x + fw * 0.35,
          h
        );
        ctx.closePath();
        ctx.fill();
        col.phase += col.speed * 16;
      });
      raf = window.requestAnimationFrame(draw);
    };
    raf = window.requestAnimationFrame(draw);
    return () => {
      alive = false;
      window.cancelAnimationFrame(raf);
    };
  }, [raging]);

  return (
    <div className={`nook-burn${raging ? " is-raging" : ""}`} aria-hidden="true">
      <canvas ref={canvasRef} className="nook-burn-canvas" />
      <div className="nook-burn-spread" />
      <div className="nook-burn-veil" />
      <div className="nook-burn-tongues">
        {tongues.map((t) => (
          <span
            key={t.id}
            className="nook-burn-tongue"
            style={{
              left: `${t.left}%`,
              "--tongue-delay": `${t.delay}s`,
              "--tongue-h": `${t.height}%`,
              "--tongue-w": `${t.width}%`,
            }}
          />
        ))}
      </div>
      <div className="nook-burn-embers">
        {embers.map((e) => (
          <span
            key={e.id}
            className="nook-burn-ember"
            style={{
              left: `${e.x}%`,
              "--ember-delay": `${e.delay}s`,
              "--ember-duration": `${e.duration}s`,
              "--ember-size": `${e.size}px`,
              "--ember-drift": `${e.drift}px`,
            }}
          />
        ))}
      </div>
      <div className="nook-burn-ash" />
    </div>
  );
}

let candleUid = 0;

function Candle({
  className = "",
  size = "md",
  puffing = false,
  extinguished = false,
  igniting = false,
  dragonlit = false,
  onPuff,
  wickRef,
}) {
  const uidRef = useRef(null);
  if (uidRef.current === null) uidRef.current = `wax${++candleUid}`;
  const uid = uidRef.current;

  const handleActivate = (e) => {
    e.stopPropagation();
    if (e.type === "keydown") {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
    }
    onPuff?.();
  };

  const stateClass = extinguished
    ? " candle--out"
    : igniting
      ? " candle--ignite"
      : puffing
        ? " candle--puff"
        : "";
  const magicClass = dragonlit ? " is-dragonlit" : "";

  return (
    <button
      type="button"
      className={`candle candle--${size}${stateClass}${magicClass}${
        className ? ` ${className}` : ""
      }`}
      aria-label="Candle"
      onClick={handleActivate}
      onKeyDown={handleActivate}
    >
      <span className="candle-live" aria-hidden="true">
        <span className="candle-glow" />
        <span className="candle-glow-core" />
        <span className="candle-flame">
          <span className="candle-flame-blue" />
          <span className="candle-flame-inner" />
        </span>
        <span className="candle-smoke">
          <span className="candle-smoke-wisp candle-smoke-wisp--a" />
          <span className="candle-smoke-wisp candle-smoke-wisp--b" />
          <span className="candle-smoke-wisp candle-smoke-wisp--c" />
        </span>
        <span className="candle-wick" ref={wickRef} />
      </span>
      <svg
        className="candle-body"
        viewBox="0 0 100 268"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id={`${uid}-brass`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1a1008" />
            <stop offset="14%" stopColor="#4a3014" />
            <stop offset="32%" stopColor="#8a5e2a" />
            <stop offset="48%" stopColor="#c4a05a" />
            <stop offset="62%" stopColor="#8a5e2a" />
            <stop offset="82%" stopColor="#4a3014" />
            <stop offset="100%" stopColor="#160e06" />
          </linearGradient>
          <linearGradient id={`${uid}-brass-v`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e8c888" />
            <stop offset="28%" stopColor="#b08440" />
            <stop offset="70%" stopColor="#6a441c" />
            <stop offset="100%" stopColor="#2a180c" />
          </linearGradient>
          <linearGradient id={`${uid}-cyl`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#6a5848" />
            <stop offset="14%" stopColor="#c4b090" />
            <stop offset="32%" stopColor="#f6eee0" />
            <stop offset="50%" stopColor="#eadcc6" />
            <stop offset="74%" stopColor="#c4ae92" />
            <stop offset="100%" stopColor="#5e4c3c" />
          </linearGradient>
          <linearGradient id={`${uid}-drip`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#6e5a48" />
            <stop offset="26%" stopColor="#f8f1e4" />
            <stop offset="54%" stopColor="#e4d2b8" />
            <stop offset="100%" stopColor="#7a6654" />
          </linearGradient>
          <radialGradient id={`${uid}-head`} cx="34%" cy="22%" r="74%">
            <stop offset="0%" stopColor="#fffaf2" />
            <stop offset="48%" stopColor="#ead8c0" />
            <stop offset="100%" stopColor="#7a6652" />
          </radialGradient>
          <radialGradient id={`${uid}-well`} cx="48%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#fff4c8" />
            <stop offset="42%" stopColor="#e8c888" />
            <stop offset="100%" stopColor="#b09060" />
          </radialGradient>
          <radialGradient id={`${uid}-sss`} cx="50%" cy="8%" r="58%">
            <stop offset="0%" stopColor="#fff0c0" stopOpacity="0.92" />
            <stop offset="32%" stopColor="#ffd078" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#eadcc6" stopOpacity="0" />
          </radialGradient>
          <clipPath id={`${uid}-melt`}>
            <rect className="wax-melt-window" x="-24" y="0" width="148" height="280" />
          </clipPath>
          <clipPath id={`${uid}-below-flame`}>
            <rect x="-24" y="30" width="148" height="250" />
          </clipPath>
          <clipPath id={`${uid}-flow-a`}>
            <rect x="26" y="32" width="18" height="4">
              <animate
                attributeName="height"
                from="4"
                to="112"
                dur="16s"
                fill="freeze"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.25 0.08 0.2 1"
              />
            </rect>
          </clipPath>
          <clipPath id={`${uid}-flow-b`}>
            <rect x="54" y="32" width="20" height="4">
              <animate
                attributeName="height"
                from="4"
                to="98"
                dur="20s"
                begin="1.2s"
                fill="freeze"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.25 0.08 0.2 1"
              />
            </rect>
          </clipPath>
          <clipPath id={`${uid}-flow-c`}>
            <rect x="40" y="32" width="16" height="3">
              <animate
                attributeName="height"
                from="3"
                to="58"
                dur="11s"
                begin="0.5s"
                fill="freeze"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.25 0.08 0.2 1"
              />
            </rect>
          </clipPath>
          <clipPath id={`${uid}-flow-d`}>
            <rect x="52" y="150" width="20" height="3">
              <animate
                attributeName="height"
                from="3"
                to="40"
                dur="12s"
                begin="14s"
                fill="freeze"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.25 0.08 0.2 1"
              />
            </rect>
          </clipPath>
        </defs>

        {/* Antique brass chamberstick: flared cup, knop, three circular tiers. */}
        <ellipse cx="50" cy="262.4" rx="32" ry="5.4" fill={`url(#${uid}-brass)`} />
        <path
          fill={`url(#${uid}-brass)`}
          d="M18 252c0 6.4 14.4 11.2 32 11.2s32-4.8 32-11.2v-8c0 5.2-14.4 9-32 9s-32-3.8-32-9z"
        />
        <ellipse cx="50" cy="252" rx="32" ry="6.2" fill={`url(#${uid}-brass)`} />
        <ellipse cx="50" cy="249.6" rx="27.5" ry="3.6" fill="#2a180c" opacity="0.38" />
        <ellipse cx="40" cy="250.4" rx="7" ry="1.6" fill="#e8c878" opacity="0.32" />

        <path
          fill={`url(#${uid}-brass)`}
          d="M27 251.2c1.6-16 8.8-22.4 23-23.6 14.2 1.2 21.4 7.6 23 23.6z"
        />
        <ellipse cx="50" cy="227.8" rx="19.5" ry="4.4" fill={`url(#${uid}-brass-v)`} />
        <ellipse cx="50" cy="226.4" rx="16" ry="2.4" fill="#3a2410" opacity="0.32" />
        <ellipse cx="43" cy="227" rx="5" ry="1.2" fill="#f0d090" opacity="0.34" />

        <path
          fill={`url(#${uid}-brass)`}
          d="M36.5 227.4v-10.4c0-2.6 6-4.6 13.5-4.6s13.5 2 13.5 4.6v10.4z"
        />
        <ellipse cx="50" cy="217" rx="13.6" ry="3.2" fill={`url(#${uid}-brass-v)`} />
        <ellipse cx="50" cy="216" rx="11" ry="1.8" fill="#2a180c" opacity="0.3" />

        <ellipse cx="50" cy="205.4" rx="8.2" ry="5.6" fill={`url(#${uid}-brass)`} />
        <ellipse cx="50" cy="202.6" rx="6.6" ry="2.4" fill={`url(#${uid}-brass-v)`} />
        <ellipse cx="47" cy="202.8" rx="2.2" ry="0.9" fill="#f4dc9c" opacity="0.4" />

        <path
          fill={`url(#${uid}-brass)`}
          d="M45.4 178c.2 8.4 1.6 12.2 4.6 16.2 1.4 1.8 1.6 3.2 1.6 6.4v3.2h-11.2v-3.2c0-3.2.2-4.6 1.6-6.4 3-4 4.4-7.8 4.6-16.2z"
        />

        <path
          fill={`url(#${uid}-brass)`}
          d="M31 168c0-5.2 8.4-9.2 19-9.2s19 4 19 9.2v16.5c0 4.2-8.4 6.8-19 6.8s-19-2.6-19-6.8z"
        />
        <ellipse cx="50" cy="168" rx="20.4" ry="5.2" fill={`url(#${uid}-brass-v)`} />
        <ellipse cx="50" cy="166.6" rx="16.2" ry="3.4" fill="#4a3014" opacity="0.42" />
        <ellipse cx="50" cy="165.6" rx="13.2" ry="2.4" fill="#22140a" opacity="0.4" />
        <ellipse cx="42" cy="167.2" rx="5.4" ry="1.4" fill="#f0d090" opacity="0.4" />

        <g className="wax-column" clipPath={`url(#${uid}-melt)`}>
          <g clipPath={`url(#${uid}-below-flame)`}>
            <path
              fill={`url(#${uid}-cyl)`}
              d="M38 30h24v136.5c0 4.2-4.8 6.8-12 6.8s-12-2.6-12-6.8z"
            />
            <path
              fill={`url(#${uid}-sss)`}
              d="M38 30h24v36c-3.4 7-7 9.6-12 9.6s-8.6-2.6-12-9.6z"
            />
            <ellipse cx="50" cy="30" rx="12" ry="3.1" fill={`url(#${uid}-cyl)`} />

            <path
              className="wax-drip-line wax-drip-line--a"
              d="M35 32c-2.4 18-3.2 40-1.6 58 1.4 16 0.6 28 0.8 38"
              fill="none"
              stroke={`url(#${uid}-drip)`}
              strokeWidth="4.2"
              strokeLinecap="round"
            />
            <path
              className="wax-drip-line wax-drip-line--b"
              d="M64 32c2.6 16 3.4 34 2.2 52-1 14 0.4 24 0.2 32"
              fill="none"
              stroke={`url(#${uid}-drip)`}
              strokeWidth="3.8"
              strokeLinecap="round"
            />
            <path
              className="wax-drip-line wax-drip-line--c"
              d="M47 32c-1.4 10-1.8 20-0.6 30 0.8 8 0.4 14 0.2 18"
              fill="none"
              stroke={`url(#${uid}-drip)`}
              strokeWidth="3.2"
              strokeLinecap="round"
            />

            <g clipPath={`url(#${uid}-flow-a)`}>
              <path
                fill={`url(#${uid}-drip)`}
                d="M38 32c-4.2 4.2-6.4 16.8-5.2 34.6 1 15.6-2.2 32.4 1.2 44.2 2.4 8.4 6.6 8 7-1.4.4-8.2-1.6-24.8.4-43.6 1.4-14.4 3.2-24.6 4.6-31.2-1.8-2.8-4.4-3.4-8-2.6z"
              />
              <path
                fill={`url(#${uid}-head)`}
                d="M34.2 108.8c-2.4 1.2-3.6 4.2-2.6 7.2 1.2 3.6 4.4 5.2 6.8 3.4 2.2-1.6 2.4-5.2.8-7.6-1.4-2.2-3.2-3.8-5-3z"
              />
            </g>
            <g clipPath={`url(#${uid}-flow-b)`}>
              <path
                fill={`url(#${uid}-drip)`}
                d="M61.8 32c4 4 6 15.4 4.8 31.2-1 13.6 2.4 26.8.2 35.6-2 8-6.4 7.2-6.8-1.2-.4-11.4 1.2-25.6.6-40.2-.4-9.6-1.6-16.8 1.2-21.6.6-1 1.8-2.6 0-3.8z"
              />
              <path
                fill={`url(#${uid}-head)`}
                d="M65.4 97.2c2.2 1.2 3.4 4 2.4 6.8-1.2 3.4-4.2 4.8-6.4 3.2-2-1.4-2.4-4.8-.8-7.2 1.4-2 3-3.6 4.8-2.8z"
              />
            </g>
            <g clipPath={`url(#${uid}-flow-c)`}>
              <path
                fill={`url(#${uid}-drip)`}
                d="M46.2 32c-2.6 4.2-3.4 12.8-2 22.6.8 6.6-1.2 12.6 1.6 15 2.6 2.2 4.2-2.6 3.6-7.8-.7-8.4 1.4-17.2 2-22.2-.8-3-2.8-4.4-5.2-7.6z"
              />
              <path
                fill={`url(#${uid}-head)`}
                d="M46.6 67.2c-1.8 1-2.8 3.2-2 5.6 1 2.8 3.4 4 5.2 2.6 1.6-1.2 1.8-4 .6-5.8-1.2-1.8-2.6-3-3.8-2.4z"
              />
            </g>

            <g className="wax-crown">
              <ellipse
                className="wax-pool"
                cx="50"
                cy="31.4"
                rx="9.2"
                ry="2.2"
                fill={`url(#${uid}-well)`}
              />
              <ellipse cx="50" cy="31.2" rx="6.2" ry="1.2" fill="#fff8e4" opacity="0.4" />
            </g>
            <ellipse cx="42.4" cy="48" rx="1.2" ry="4.6" fill="#fffdf6" opacity="0.28" />

            <ellipse cx="34.2" cy="36" rx="1.7" ry="2.3" fill={`url(#${uid}-head)`} opacity="0">
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                keyTimes="0;0.1;0.72;1"
                dur="5s"
                begin="8s"
                repeatCount="indefinite"
              />
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0; 0 68"
                dur="5s"
                begin="8s"
                repeatCount="indefinite"
              />
            </ellipse>
          </g>
        </g>

        {/* Front of the flared cup lip — seats the pillar in the holder. */}
        <path
          fill={`url(#${uid}-brass-v)`}
          d="M29.8 168c4.6 6.4 12.2 9.2 20.2 9.2s15.6-2.8 20.2-9.2c-4.4 3.6-12 5.6-20.2 5.6s-15.8-2-20.2-5.6z"
        />
        <path
          fill="#2a180c"
          opacity="0.28"
          d="M33.4 169.2c3.8 4.4 10 6.4 16.6 6.4s12.8-2 16.6-6.4c-3.6 2.4-9.8 3.8-16.6 3.8s-13-1.4-16.6-3.8z"
        />

        <g clipPath={`url(#${uid}-flow-d)`}>
          <path
            fill={`url(#${uid}-drip)`}
            d="M61.2 152c2.4 4.8 3.6 10.6 3 16.4-.5 4.4 2 8.6.6 11.8-1.6 3.6-5.2 3.2-5.6-.4-.4-4.2 1.4-9.2.8-16.2-.4-4.2-1.2-8 1.2-11.6z"
          />
          <path
            fill={`url(#${uid}-head)`}
            d="M62.2 176.4c-2.2 1.2-3.4 4-2.4 6.6 1.2 3.2 4.4 4.6 6.4 3 2-1.4 2.2-4.8.6-7.2-1.4-2-2.8-3.6-4.6-2.4z"
          />
        </g>

        <ellipse cx="62.4" cy="156" rx="1.6" ry="2.2" fill={`url(#${uid}-head)`} opacity="0">
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            keyTimes="0;0.12;0.7;1"
            dur="4.5s"
            begin="16s"
            repeatCount="indefinite"
          />
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0; 0 28"
            dur="4.5s"
            begin="16s"
            repeatCount="indefinite"
          />
        </ellipse>
      </svg>
    </button>
  );
}

// ── Easter egg: FAIRY DUST ───────────────────────────────────────────────────
// Where: absolute overlay inside .bookcase-frame (.fairy-dust), pointer-events none.
// When:  first drift at a fixed 5s after mount, then every ~5–10 minutes.
// Look:  warm-gold motes (8–12) drifting ~6–8s across the shelves.
// Note:  still spawns with prefers-reduced-motion (CSS shows a static glow).
function FairyDust({ particles }) {
  if (!particles.length) return null;
  return (
    <div className="fairy-dust" aria-hidden="true">
      {particles.map((p) => (
        <span
          key={p.id}
          className="fairy-dust-mote"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            "--drift-x": `${p.dx}px`,
            "--drift-y": `${p.dy}px`,
            "--mote-delay": `${p.delay}s`,
            "--mote-duration": `${p.duration}s`,
            "--mote-size": `${p.size}px`,
          }}
        />
      ))}
    </div>
  );
}

const THEME_SRC = "/audio/out-of-africa.mp3";
const THEME_VOLUME = 0.34;
const THEME_MUTE_KEY = "nook-theme-muted";

function readThemeMuted() {
  try {
    return sessionStorage.getItem(THEME_MUTE_KEY) === "1";
  } catch (err) {
    return false;
  }
}

function writeThemeMuted(muted) {
  try {
    sessionStorage.setItem(THEME_MUTE_KEY, muted ? "1" : "0");
  } catch (err) {
    // Private mode can block sessionStorage.
  }
}

function NookTheme() {
  const audioRef = useRef(null);
  const mutedRef = useRef(readThemeMuted());
  const [muted, setMuted] = useState(() => mutedRef.current);

  useEffect(() => {
    const audio = new Audio(THEME_SRC);
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = 0;
    audio.muted = mutedRef.current;
    audioRef.current = audio;

    let fadeFrame = 0;
    let cancelled = false;

    const fadeTo = (target, ms) => {
      cancelAnimationFrame(fadeFrame);
      const start = performance.now();
      const from = audio.volume;
      const tick = (now) => {
        if (cancelled) return;
        const t = Math.min(1, (now - start) / ms);
        audio.volume = from + (target - from) * t;
        if (t < 1) fadeFrame = requestAnimationFrame(tick);
      };
      fadeFrame = requestAnimationFrame(tick);
    };

    let started = false;
    const stopUnlock = () => {
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("keydown", tryPlay);
    };
    const tryPlay = (event) => {
      if (cancelled || started) return;
      if (event?.target?.closest?.(".nook-theme-mute")) return;
      const play = audio.play();
      if (play && typeof play.then === "function") {
        play
          .then(() => {
            if (cancelled || started) return;
            started = true;
            stopUnlock();
            if (audio.muted) audio.volume = THEME_VOLUME;
            else fadeTo(THEME_VOLUME, 2400);
          })
          .catch(() => {});
      }
    };

    tryPlay();
    window.addEventListener("pointerdown", tryPlay);
    window.addEventListener("keydown", tryPlay);

    return () => {
      cancelled = true;
      cancelAnimationFrame(fadeFrame);
      stopUnlock();
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audioRef.current = null;
    };
  }, []);

  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    writeThemeMuted(next);
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = next;
    if (next) return;
    audio.volume = THEME_VOLUME;
    audio.play().catch(() => {});
  };

  return (
    <div className={`nook-theme${muted ? " is-muted" : ""}`}>
      <span className="nook-theme-caption" aria-hidden="true">
        Out of Africa
      </span>
      <button
        type="button"
        className="nook-theme-mute"
        onClick={toggleMute}
        aria-label={muted ? "Unmute the theme" : "Mute the theme"}
        aria-pressed={muted}
      >
        <svg className="nook-theme-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M5.2 9.1h2.7L11.6 6v12l-3.7-3.1H5.2V9.1z"
          />
          {muted ? (
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              d="M15.2 9.2l5.1 5.6M20.3 9.2l-5.1 5.6"
            />
          ) : (
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="1.55"
              strokeLinecap="round"
              className="nook-theme-waves"
              d="M14.8 9.1c1.45 1.25 1.45 4.55 0 5.8M17.5 7c2.5 2.15 2.5 7.85 0 10"
            />
          )}
        </svg>
      </button>
    </div>
  );
}

function TheShelf() {
  const [activeBook, setActiveBook] = useState(null);
  const [coverFailed, setCoverFailed] = useState(false);
  const [fairyParticles, setFairyParticles] = useState([]);
  const [centerOut, setCenterOut] = useState(false);
  const [centerIgniting, setCenterIgniting] = useState(false);
  const [dragonPlaying, setDragonPlaying] = useState(false);
  const [bottomOut, setBottomOut] = useState(false);
  const [bottomIgniting, setBottomIgniting] = useState(false);
  const [archerPlaying, setArcherPlaying] = useState(false);
  const [topIgniting, setTopIgniting] = useState(false);
  const [lit, setLit] = useState({ top: false, dragon: false, archer: false });
  const [nookPhase, setNookPhase] = useState("shelf");
  const [tippedCount, setTippedCount] = useState(0);

  const fairyTimersRef = useRef([]);
  const centerWickRef = useRef(null);
  const bottomWickRef = useRef(null);
  const bottomBoardRef = useRef(null);
  const sequenceTimersRef = useRef([]);
  const sequenceStartedRef = useRef(false);
  const topIgniteTimerRef = useRef(null);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/the-shelf",
      title: "Book Nook",
    });
  }, []);

  // Warm cover images so the open-book panel is instant.
  useEffect(() => {
    SECTIONS.forEach((section) => {
      section.books.forEach((book) => {
        if (!book.cover) return;
        const img = new Image();
        img.src = coverSrc(book.cover);
      });
    });
  }, []);

  useEffect(() => {
    if (!activeBook) return undefined;
    setCoverFailed(false);
    const onKeyDown = (e) => {
      if (e.key === "Escape") setActiveBook(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeBook]);

  // Ambient fairy dust — first drift at a fixed 5s, then rare.
  // Still runs with prefers-reduced-motion (static/simple glow via CSS).
  useEffect(() => {
    let cancelled = false;
    const reduced = prefersReducedMotion();

    const clearFairyTimers = () => {
      fairyTimersRef.current.forEach((id) => clearTimeout(id));
      fairyTimersRef.current = [];
    };

    const track = (id) => {
      fairyTimersRef.current.push(id);
      return id;
    };

    const spawnDust = () => {
      if (cancelled) return;
      const count = 8 + Math.floor(Math.random() * 5); // 8–12
      const particles = Array.from({ length: count }, (_, i) => ({
        id: `${Date.now()}-${i}`,
        x: randomBetween(8, 88),
        y: randomBetween(12, 72),
        dx: randomBetween(40, 120) * (Math.random() > 0.5 ? 1 : -1),
        dy: randomBetween(-50, -18),
        delay: reduced ? 0 : randomBetween(0, 0.6),
        // Drift ~6–8s (shorter static hold when reduced-motion).
        duration: reduced ? 3.2 : randomBetween(6, 8),
        // Warm gold dots — faintly glowing, clearly findable
        size: randomBetween(4, 6),
      }));
      setFairyParticles(particles);
      const clearMs = reduced ? 3600 : 9000;
      track(
        setTimeout(() => {
          if (!cancelled) setFairyParticles([]);
        }, clearMs)
      );
    };

    const scheduleNext = () => {
      // After the first visit: every ~5–10 minutes.
      const wait = randomBetween(5 * 60 * 1000, 10 * 60 * 1000);
      track(
        setTimeout(() => {
          spawnDust();
          scheduleNext();
        }, wait)
      );
    };

    // Fixed 5s first spawn — reliable for testing; not skipped for reduced-motion.
    track(
      setTimeout(() => {
        spawnDust();
        scheduleNext();
      }, 5000)
    );

    return () => {
      cancelled = true;
      clearFairyTimers();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (topIgniteTimerRef.current) clearTimeout(topIgniteTimerRef.current);
      sequenceTimersRef.current.forEach((id) => clearTimeout(id));
    };
  }, []);

  const lightCandle = useCallback((key) => {
    setLit((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const restoreNook = useCallback(() => {
    sequenceTimersRef.current.forEach((id) => clearTimeout(id));
    sequenceTimersRef.current = [];
    sequenceStartedRef.current = false;
    setNookPhase("shelf");
    setTippedCount(0);
    setLit({ top: false, dragon: false, archer: false });
    setTopIgniting(false);
    setCenterOut(false);
    setCenterIgniting(false);
    setDragonPlaying(false);
    setBottomOut(false);
    setBottomIgniting(false);
    setArcherPlaying(false);
  }, []);

  useEffect(() => {
    if (
      nookPhase !== "complete" &&
      nookPhase !== "burn" &&
      nookPhase !== "tip" &&
      nookPhase !== "pause"
    ) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (activeBook) return;
      e.preventDefault();
      restoreNook();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [nookPhase, activeBook, restoreNook]);

  useEffect(() => {
    if (!lit.top || !lit.dragon || !lit.archer) {
      return undefined;
    }
    // Hold the inferno until the dragon / archer have left the frame.
    if (dragonPlaying || archerPlaying) return undefined;
    if (sequenceStartedRef.current) return undefined;
    sequenceStartedRef.current = true;

    const later = (fn, ms) => {
      const id = setTimeout(fn, ms);
      sequenceTimersRef.current.push(id);
    };

    const reduced = prefersReducedMotion();
    setNookPhase("pause");
    const pauseMs = reduced ? 400 : 1100;
    later(() => {
      setNookPhase("tip");
      setTippedCount(1);
    }, pauseMs);
    later(() => setTippedCount(2), pauseMs + (reduced ? 220 : 560));
    later(() => setTippedCount(3), pauseMs + (reduced ? 440 : 1120));
    later(() => setNookPhase("burn"), pauseMs + (reduced ? 640 : 1680));
    later(() => {
      setTippedCount(0);
      setNookPhase("complete");
    }, pauseMs + (reduced ? 1600 : 5800));
    return undefined;
  }, [lit.top, lit.dragon, lit.archer, dragonPlaying, archerPlaying]);

  const ritualComplete = lit.top && lit.dragon && lit.archer;
  const candlesLocked = nookPhase !== "shelf" || ritualComplete;

  const handleTopCandle = () => {
    if (candlesLocked || lit.top) return;
    lightCandle("top");
    setTopIgniting(true);
    if (topIgniteTimerRef.current) clearTimeout(topIgniteTimerRef.current);
    topIgniteTimerRef.current = setTimeout(() => {
      setTopIgniting(false);
      topIgniteTimerRef.current = null;
    }, 600);
  };

  const handleCenterCandle = () => {
    if (candlesLocked || dragonPlaying) return;
    unlockNookWhoosh();
    setCenterOut(true);
    setCenterIgniting(false);
    setDragonPlaying(true);
  };

  const handleBottomCandle = () => {
    if (candlesLocked || archerPlaying) return;
    setBottomOut(true);
    setBottomIgniting(false);
    setArcherPlaying(true);
  };

  const bookcaseClass = [
    "bookcase",
    nookPhase !== "shelf" && nookPhase !== "complete"
      ? `bookcase--${nookPhase}`
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showBurn = nookPhase === "tip" || nookPhase === "burn";

  return (
    <div className="TheShelf">
      <Helmet bodyAttributes={{ style: "background-color: #1a1714" }}>
        <title>Book Nook</title>
      </Helmet>
      <main>
        <section>
          <Link to="/" className="back-link">
            <span className="back-caret">&#x3008;</span> Home
          </Link>

          <h1>Book Nook.</h1>
          <p className="shelf-subtitle">A wee glimpse into my library</p>
          <NookTheme />

          <div className={bookcaseClass}>
            <div className="bookcase-crown" aria-hidden="true">
              <span className="crown-cap" />
              <span className="crown-cyma" />
              <span className="crown-bed" />
            </div>
            <div className="bookcase-body">
              <div className="bookcase-column bookcase-column--left" aria-hidden="true" />
              <div className="bookcase-column bookcase-column--right" aria-hidden="true" />
              <div
                className={`bookcase-frame${
                  nookPhase === "tip" || nookPhase === "burn"
                    ? " bookcase-frame--catching"
                    : ""
                }`}
              >
              <FairyDust particles={fairyParticles} />
              <NookDragon
                playing={dragonPlaying}
                wickRef={centerWickRef}
                onRelight={() => {
                  setCenterOut(false);
                  setCenterIgniting(true);
                  lightCandle("dragon");
                }}
                onDone={() => {
                  setDragonPlaying(false);
                  setCenterIgniting(false);
                }}
              />
              <NookArcher
                playing={archerPlaying}
                wickRef={bottomWickRef}
                boardRef={bottomBoardRef}
                onRelight={() => {
                  setBottomOut(false);
                  setBottomIgniting(true);
                  lightCandle("archer");
                }}
                onDone={() => {
                  setArcherPlaying(false);
                  setBottomIgniting(false);
                }}
              />
              {showBurn && <NookBurnOverlay raging={nookPhase === "burn"} />}

              {SECTIONS.map((section, i) => {
                // Candles live in .shelf-bay (outside .shelf-books) so overflow
                // on the book row cannot clip flame, glow, or smoke.
                const candleSide =
                  i === TOP_CANDLE_SHELF
                    ? "left"
                    : i === CENTER_CANDLE_SHELF
                      ? "right"
                      : i === BOTTOM_CANDLE_SHELF
                        ? "left"
                        : null;
                const candleSize =
                  i === TOP_CANDLE_SHELF
                    ? "md"
                    : i === CENTER_CANDLE_SHELF
                      ? "lg"
                      : "sm";
                const isTop = i === TOP_CANDLE_SHELF;
                const isCenter = i === CENTER_CANDLE_SHELF;
                const isBottom = i === BOTTOM_CANDLE_SHELF;
                const tipping =
                  (isTop && tippedCount >= 1) ||
                  (isCenter && tippedCount >= 2) ||
                  (isBottom && tippedCount >= 3);
                return (
                  <div className="shelf" key={section.category}>
                    <div className="shelf-bay">
                      {candleSide && (
                        <Candle
                          className={`candle--on-shelf candle--${candleSide}${
                            isTop ? " candle--top" : ""
                          }${isCenter ? " candle--center" : ""}${
                            isBottom ? " candle--bottom" : ""
                          }${
                            isBottom && lit.archer ? " is-archerlit" : ""
                          }${tipping ? " candle--tipping" : ""}`}
                          size={candleSize}
                          extinguished={
                            (isCenter && centerOut) || (isBottom && bottomOut)
                          }
                          igniting={
                            (isTop && topIgniting) ||
                            (isCenter && centerIgniting) ||
                            (isBottom && bottomIgniting)
                          }
                          dragonlit={(isTop && lit.top) || (isCenter && lit.dragon)}
                          wickRef={
                            isCenter
                              ? centerWickRef
                              : isBottom
                                ? bottomWickRef
                                : undefined
                          }
                          onPuff={
                            isTop
                              ? handleTopCandle
                              : isCenter
                                ? handleCenterCandle
                                : isBottom
                                  ? handleBottomCandle
                                  : undefined
                          }
                        />
                      )}
                      <div className="shelf-books" role="list">
                      {section.books.map((book, bookIndex) => {
                        const v = visualFor(book);
                        const classNames = ["book", v.material];
                        if (v.foil) classNames.push("foil");
                        if (v.lightSpine) classNames.push("light-spine");
                        return (
                          <button
                            key={`${section.category}-${book.title}`}
                            type="button"
                            className={classNames.join(" ")}
                            role="listitem"
                            style={{
                              "--spine": v.color,
                              "--fall-delay": `${(i * 0.09 + bookIndex * 0.04).toFixed(2)}s`,
                              height: `${v.height}px`,
                              width: `${v.width}px`,
                            }}
                            onClick={() => {
                              if (
                                nookPhase !== "shelf" &&
                                nookPhase !== "complete"
                              ) {
                                return;
                              }
                              setActiveBook(book);
                            }}
                            aria-label={`${book.title} by ${book.author}`}
                          >
                            <span className="book-cap" aria-hidden="true" />
                            <span className="book-spine-mid">
                              <span className="book-title">
                                {book.spineTitle || book.title}
                              </span>
                              <span className="book-author">{book.author}</span>
                            </span>
                            <span className="book-foot" aria-hidden="true" />
                          </button>
                        );
                      })}
                      </div>
                    </div>
                    <div className="shelf-deck" aria-hidden="true">
                      <div className="shelf-reflections">
                        {section.books.map((book) => {
                          const v = visualFor(book);
                          return (
                            <span
                              key={`ref-${section.category}-${book.title}`}
                              className="book-reflection"
                              style={{
                                width: `${v.width}px`,
                                background: v.color,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                    <div
                      className="shelf-board"
                      ref={isBottom ? bottomBoardRef : undefined}
                    >
                      <span className="shelf-label">{section.category}</span>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
            <div className="bookcase-plinth" aria-hidden="true">
              <span className="plinth-step plinth-step--a" />
              <span className="plinth-step plinth-step--b" />
              <span className="plinth-step plinth-step--c" />
            </div>
          </div>
        </section>
      </main>

      {activeBook &&
        createPortal(
          <div
            className="book-panel-overlay"
            onClick={() => setActiveBook(null)}
            role="presentation"
          >
            <div
              className="book-panel"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={activeBook.title}
            >
              <button
                type="button"
                className="panel-close"
                onClick={() => setActiveBook(null)}
                aria-label="Close"
              >
                &times;
              </button>

              <div
                className="book-cover"
                style={{ backgroundColor: visualFor(activeBook).color }}
              >
                {activeBook.cover && !coverFailed ? (
                  <img
                    src={coverSrc(activeBook.cover)}
                    alt={`${activeBook.title} cover`}
                    decoding="async"
                    onError={() => setCoverFailed(true)}
                  />
                ) : (
                  <div className="book-cover-fallback">
                    <span className="cover-title">{activeBook.title}</span>
                    <span className="cover-author">{activeBook.author}</span>
                  </div>
                )}
              </div>

              <div className="book-detail">
                <h2>{activeBook.title}</h2>
                <p className="book-byline">{activeBook.author}</p>
                {activeBook.note && (
                  <p className="book-note">{activeBook.note}</p>
                )}
                <a
                  className="goodreads-link"
                  href={goodreadsUrl(activeBook)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Find it on Goodreads
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default TheShelf;
